import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@cartcloser/ui';
import { listMappingGroups, rebuildMapping, setMappingReview } from '../lib/api.js';

export const Route = createFileRoute('/admin/$slug/mapping')({
  component: MappingPage,
});

function MappingPage() {
  const { slug } = Route.useParams();
  const queryClient = useQueryClient();
  const groups = useQuery({ queryKey: ['mapping', slug], queryFn: () => listMappingGroups(slug) });

  const rebuild = useMutation({
    mutationFn: () => rebuildMapping(slug),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mapping', slug] }),
  });

  const review = useMutation({
    mutationFn: (v: { id: string; status: 'confirmed' | 'rejected' }) =>
      setMappingReview(slug, v.id, v.status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mapping', slug] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Cross-SKU mapping</h1>
          <p className="text-sm text-muted-foreground">
            SKUs sharing a physical signature or an explicit model attribute are collapsed to one
            recommendation, with the others as in-stock fallback.
          </p>
        </div>
        <Button loading={rebuild.isPending} onClick={() => rebuild.mutate()}>
          Rebuild
        </Button>
      </div>

      {rebuild.data ? (
        <p className="text-sm text-muted-foreground">
          Found {rebuild.data.groups} groups across {rebuild.data.mappedProducts} SKUs.
        </p>
      ) : null}

      <div className="grid gap-3">
        {groups.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : groups.data && groups.data.length > 0 ? (
          groups.data.map((g) => (
            <Card key={g.id}>
              <CardHeader className="flex flex-col gap-2 space-y-0 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-base">
                  Group · {g.confidence}% confidence
                </CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant={
                      g.reviewStatus === 'confirmed'
                        ? 'success'
                        : g.reviewStatus === 'rejected'
                          ? 'destructive'
                          : 'secondary'
                    }
                  >
                    {g.reviewStatus}
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => review.mutate({ id: g.id, status: 'confirmed' })}
                  >
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => review.mutate({ id: g.id, status: 'rejected' })}
                  >
                    Reject
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  {g.members.map((m) => (
                    <li key={m.id} className="flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{m.sku}</span>
                      {m.title}
                      {m.isCanonical ? <Badge variant="outline">canonical</Badge> : null}
                      {!m.inStock ? <Badge variant="secondary">out of stock</Badge> : null}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardHeader>
              <CardDescription>
                No mapping groups yet. Import products with shared dimensions/specs, then Rebuild.
              </CardDescription>
            </CardHeader>
          </Card>
        )}
      </div>
    </div>
  );
}
